import React from 'react';
import { useEffectiveIdentity } from '@/hooks/useEffectiveIdentity';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

interface RoleGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'department_manager' | 'procurement_manager' | 'provider_manager' | 'qa_manager' | 'ma_manager' | 'tech_apps' | 'tech_infrastructure' | 'creative_manager';
  allowedRoles?: ('admin' | 'department_manager' | 'procurement_manager' | 'provider_manager' | 'qa_manager' | 'ma_manager' | 'tech_apps' | 'tech_infrastructure' | 'creative_manager')[];
  requiredLab?: string;
  requiredPage?: string;
  fallback?: React.ReactNode;
}

export function RoleGuard({ 
  children, 
  requiredRole,
  allowedRoles,
  requiredLab,
  requiredPage,
  fallback 
}: RoleGuardProps) {
  const { role, loading, canAccessLab, isAdmin, effectiveRole, effectiveDepartment, impersonationMode, allowedPages } = useEffectiveIdentity();
  console.log('[RoleGuard] context', { role, isAdmin, requiredRole, allowedRoles, requiredLab, requiredPage, effectiveRole, effectiveDepartment, impersonationMode, allowedPages });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Check role requirement (single role)
  if (requiredRole && role !== requiredRole && !isAdmin) {
    return fallback || (
      <Alert variant="destructive" className="m-6">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to access this section.
        </AlertDescription>
      </Alert>
    );
  }

  // Check role requirement (multiple allowed roles)
  if (allowedRoles && role && !allowedRoles.includes(role) && !isAdmin) {
    return fallback || (
      <Alert variant="destructive" className="m-6">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to access this section.
        </AlertDescription>
      </Alert>
    );
  }

  // Check lab access requirement
  if (requiredLab && !canAccessLab(requiredLab)) {
    return fallback || (
      <Alert variant="destructive" className="m-6">
        <ShieldAlert className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>
          You don't have permission to access this lab.
        </AlertDescription>
      </Alert>
    );
  }

  // Check page access requirement (for impersonation)
  if (requiredPage) {
    const hasAccess = allowedPages.includes('all') || allowedPages.includes(requiredPage);
    if (!hasAccess) {
      return fallback || (
        <Alert variant="destructive" className="m-6">
          <ShieldAlert className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            You don't have permission to access this page.
          </AlertDescription>
        </Alert>
      );
    }
  }

  return <>{children}</>;
}
